import type { PlannerFirstWrite } from './planner-first-writes.js';

type RequiredField = {
  readonly field: string;
  readonly property: string;
  readonly envelopeField: string;
  readonly reason: string;
  readonly aliases?: readonly string[];
};

/** Typed write completeness is server-owned; aliases only preserve the existing wire contract. */
export const PLANNER_WRITE_REQUIRED: Readonly<
  Record<PlannerFirstWrite['capability'], readonly RequiredField[]>
> = {
  create_task: [
    { field: 'title', property: 'title', envelopeField: 'title', reason: 'Specify the task title' },
  ],
  complete_task: [
    {
      field: 'target.name',
      property: 'target',
      envelopeField: 'taskId',
      reason: 'Specify the task to change',
    },
    {
      field: 'status',
      property: 'status',
      envelopeField: 'status',
      reason: 'Specify completion status',
    },
  ],
  reschedule_task: [
    {
      field: 'target.name',
      property: 'target',
      envelopeField: 'taskId',
      reason: 'Specify the task to change',
    },
    {
      field: 'dueAt',
      property: 'dueAt',
      envelopeField: 'dueAt',
      reason: 'Specify the new deadline',
      aliases: ['date', 'time'],
    },
  ],
  create_contact: [
    {
      field: 'firstName',
      property: 'firstName',
      envelopeField: 'firstName',
      reason: 'Specify the contact first name',
    },
  ],
  add_note: [
    {
      field: 'target.name',
      property: 'target',
      envelopeField: 'target.name',
      reason: 'Specify one scoped target',
    },
    { field: 'body', property: 'body', envelopeField: 'body', reason: 'Specify the note body' },
  ],
  change_deal_stage: [
    {
      field: 'target.name',
      property: 'target',
      envelopeField: 'target.name',
      reason: 'Specify one scoped target',
    },
    {
      field: 'stage',
      property: 'stage',
      envelopeField: 'stage',
      reason: 'Specify the destination stage',
    },
  ],
};

export const PLANNER_WRITE_OPTIONAL: Readonly<
  Record<PlannerFirstWrite['capability'], readonly string[]>
> = {
  create_task: ['dueAt', 'description', 'contact', 'company'],
  complete_task: [],
  reschedule_task: [],
  create_contact: ['lastName', 'phone', 'email', 'source'],
  add_note: [],
  change_deal_stage: ['reason'],
};

export function plannerWriteOptionalFieldsOnly(
  write: PlannerFirstWrite,
  missing: readonly { field: string }[],
): boolean {
  return (
    missing.length > 0 &&
    missing.every(({ field }) => PLANNER_WRITE_OPTIONAL[write.capability].includes(field))
  );
}

export function plannerWriteMissingRules(write: PlannerFirstWrite): readonly RequiredField[] {
  return PLANNER_WRITE_REQUIRED[write.capability].filter(
    (rule) => !(rule.property in write) || write[rule.property as keyof typeof write] === undefined,
  );
}

export function plannerWriteMissingInformation(write: PlannerFirstWrite, envelope = false) {
  return plannerWriteMissingRules(write).map((rule) => ({
    field: envelope ? rule.envelopeField : rule.field,
    reason: rule.reason,
  }));
}

export function plannerWriteMissingFieldsAllowed(
  write: PlannerFirstWrite,
  missing: readonly { field: string }[],
): boolean {
  const rules = plannerWriteMissingRules(write);
  return (
    missing.length > 0 &&
    missing.every(({ field }) =>
      rules.some((rule) => rule.field === field || rule.aliases?.includes(field)),
    )
  );
}
